import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useFilters } from '@/features/backlog/use-filters';
import { writeFilterParams } from '@/features/backlog/url-state';

/**
 * Mirrors the shareable FILTER state into the query string, so the address bar always reproduces
 * what's on screen. Under the friendly routes the board and the view live in the PATH (/:slug,
 * /:slug/table), so this owns only the filter query params — and it cleans up the legacy `board`
 * param left over from the old query-based scheme.
 *
 * The `?item=<key>` param (the item open in the details panel) is owned by the views' useItemUrlSync,
 * NOT here — so this deliberately PRESERVES it (both the guard's `desired` and the write copy `prev`
 * forward, only rewriting the filter keys), rather than stripping it as the old scheme did.
 *
 * One-way: state → URL. FiltersProvider seeds itself FROM the URL on load. It writes only when the
 * canonical query differs (react-router mints a fresh location on every navigation, so an
 * unguarded write would loop). The functional write reads `prev` so a concurrent `?item` write from
 * a view is never clobbered. `replace` keeps it out of the history stack.
 */
export default function UrlSync() {
  const { filters } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  // The global search page owns its own `?query=` param and has no board-filter state to mirror —
  // running the mirror there would strip/pollute its query string. Leave it (and its `?query`) alone.
  const skip = pathname === '/search';

  const desired = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('board'); // board is the path slug now
    writeFilterParams(next, filters);
    return next.toString();
  }, [searchParams, filters]);

  useEffect(() => {
    if (skip) return;
    if (desired !== searchParams.toString()) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('board');
          writeFilterParams(next, filters);
          return next;
        },
        { replace: true },
      );
    }
  }, [skip, desired, searchParams, filters, setSearchParams]);

  return null;
}
